<script setup>
// 📒 STOK HAREKET DEFTERİ
// Prapazar stok hareketlerinin kaydını tutmadığı için (sadece güncel bakiye var),
// varyant stoklarının 20 dk'da bir çekilen fotoğraflarının farkından türetilen
// defter: depo girişi / satış / iade dönüşü / açıklanamayan düşüş.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const error = ref('');
const movements = ref([]);
const summary = ref({});
const total = ref(0);
const lastScan = ref(null);
const trackedSkus = ref(0);
const reasonFilter = ref('');
const search = ref('');
const fromDate = ref('');
const toDate = ref('');

const REASONS = {
  entry: {
    label: 'Depo girişi',
    icon: '📦',
    cls: 'text-n-teal-11 bg-n-teal-3',
  },
  return: {
    label: 'İade/iptal dönüşü',
    icon: '↩️',
    cls: 'text-n-amber-11 bg-n-amber-3',
  },
  sale: { label: 'Satış', icon: '🛒', cls: 'text-n-slate-11 bg-n-slate-3' },
  unexplained_drop: {
    label: 'Açıklanamayan düşüş',
    icon: '⚠️',
    cls: 'text-n-ruby-11 bg-n-ruby-3',
  },
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams();
    if (reasonFilter.value) params.set('reason', reasonFilter.value);
    if (search.value.trim()) params.set('q', search.value.trim());
    if (fromDate.value) params.set('from', fromDate.value);
    if (toDate.value) params.set('to', toDate.value);
    const qs = params.toString();
    const data = await fetchMarketplaceJson(
      accountId,
      `stock_movements${qs ? `?${qs}` : ''}`
    );
    movements.value = data.movements || [];
    summary.value = data.summary || {};
    total.value = data.total || 0;
    lastScan.value = data.last_scan;
    trackedSkus.value = data.tracked_skus || 0;
  } catch (e) {
    error.value = 'Defter yüklenemedi, sayfayı yenileyin.';
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const kart = key => ({
  ...REASONS[key],
  hareket: summary.value[key]?.hareket || 0,
  adet: summary.value[key]?.adet || 0,
});
const kartlar = computed(() =>
  ['entry', 'return', 'sale', 'unexplained_drop'].map(kart)
);

const zaman = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const indir = () => {
  const basliklar = [
    'Tarih',
    'Ürün',
    'Model',
    'Renk',
    'Beden',
    'Stok Kodu',
    'Değişim',
    'Önce',
    'Sonra',
    'Sebep',
  ];
  const satirlar = movements.value.map(m => [
    zaman(m.occurredAt),
    m.title,
    m.productCode,
    m.color,
    m.size,
    m.sku,
    m.delta > 0 ? `+${m.delta}` : m.delta,
    m.qtyBefore,
    m.qtyAfter,
    m.reasonLabel,
  ]);
  const csv = [basliklar, ...satirlar]
    .map(r => r.map(h => `"${String(h ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `stok-hareketleri-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="w-full max-w-6xl p-4 mx-auto">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h1 class="text-xl font-semibold text-n-slate-12">
          📒 Stok Hareket Defteri
        </h1>
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 text-xs rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-2"
            @click="indir"
          >
            Excel/CSV indir
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-lg bg-n-brand text-white"
            :disabled="loading"
            @click="load"
          >
            {{ loading ? 'Yükleniyor…' : 'Yenile' }}
          </button>
        </div>
      </div>
      <p class="mb-4 text-xs text-n-slate-10">
        Prapazar hareket kaydı tutmadığı için stoklar 20 dakikada bir taranır,
        fark deftere yazılır. İzlenen stok kodu:
        {{ trackedSkus.toLocaleString('tr-TR') }} · Son tarama:
        {{ zaman(lastScan) }}
      </p>

      <!-- Özet kartları -->
      <div class="grid grid-cols-2 gap-3 mb-4 lg:grid-cols-4">
        <div
          v-for="k in kartlar"
          :key="k.label"
          class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
        >
          <p class="text-[11px] text-n-slate-10">{{ k.icon }} {{ k.label }}</p>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ k.adet.toLocaleString('tr-TR') }}
            <span class="text-xs font-normal text-n-slate-10">adet</span>
          </p>
          <p class="text-[11px] text-n-slate-10">{{ k.hareket }} hareket</p>
        </div>
      </div>

      <!-- Filtreler -->
      <div class="flex flex-wrap gap-2 mb-3">
        <select
          v-model="reasonFilter"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
          @change="load"
        >
          <option value="">Tüm hareketler</option>
          <option value="entry">📦 Depo girişi</option>
          <option value="return">↩️ İade/iptal dönüşü</option>
          <option value="sale">🛒 Satış</option>
          <option value="unexplained_drop">⚠️ Açıklanamayan düşüş</option>
        </select>
        <input
          v-model="search"
          placeholder="Ürün adı / model / stok kodu"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak min-w-[220px]"
          @keyup.enter="load"
        />
        <input
          v-model="fromDate"
          type="date"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
          @change="load"
        />
        <input
          v-model="toDate"
          type="date"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
          @change="load"
        />
      </div>

      <p
        v-if="error"
        class="p-3 mb-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </p>

      <!-- Defter -->
      <div
        class="overflow-x-auto rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="text-[11px] text-n-slate-10 border-b border-n-weak">
              <th class="p-2 text-left">Tarih</th>
              <th class="p-2 text-left">Ürün</th>
              <th class="p-2 text-left">Renk / Beden</th>
              <th class="p-2 text-right">Değişim</th>
              <th class="p-2 text-right">Bakiye</th>
              <th class="p-2 text-left">Sebep</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in movements"
              :key="m.id"
              class="border-b border-n-weak/50"
            >
              <td class="p-2 whitespace-nowrap text-n-slate-11">
                {{ zaman(m.occurredAt) }}
              </td>
              <td class="p-2">
                <p
                  class="truncate max-w-[280px] text-n-slate-12"
                  :title="m.title"
                >
                  {{ m.title }}
                </p>
                <p class="text-[11px] text-n-slate-10">{{ m.sku }}</p>
              </td>
              <td class="p-2 text-n-slate-11">{{ m.color }} / {{ m.size }}</td>
              <td
                class="p-2 font-semibold text-right"
                :class="m.delta > 0 ? 'text-n-teal-11' : 'text-n-ruby-11'"
              >
                {{ m.delta > 0 ? '+' : '' }}{{ m.delta }}
              </td>
              <td class="p-2 text-right text-n-slate-11">
                {{ m.qtyBefore }} → {{ m.qtyAfter }}
              </td>
              <td class="p-2">
                <span
                  class="px-2 py-0.5 rounded-full text-[11px]"
                  :class="REASONS[m.reason]?.cls"
                >
                  {{ REASONS[m.reason]?.icon }} {{ m.reasonLabel }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-if="!movements.length && !loading"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          Bu filtrede hareket yok. (Defter 4 Ağustos akşamı açıldı — hareketler
          taramalarla birlikte birikiyor.)
        </p>
      </div>
      <p v-if="total > movements.length" class="mt-2 text-xs text-n-slate-10">
        Toplam {{ total }} hareketin ilk {{ movements.length }} tanesi
        gösteriliyor — filtre daraltın veya CSV indirin.
      </p>
    </div>
  </div>
</template>
